import { useFeatureFlags } from '@shared/hooks/useFeatureFlag';
import appLogger from '@shared/logging/appLogger';
import useRepresentative from '@shared/queries/useRepresentative';
import User from '@shared/types/user';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
    user: User;
}

function redirectUser(userId: string, url: string, reason: string, navigate) {
    appLogger.logTrace({
        message: `Redirecting user: ${userId} to ${reason}`,
        properties: {
            redirectUrl: url,
        },
    });
    if (url.startsWith('http')) {
        window.location.href = url; // External or non-React route
    } else {
        navigate(url, { replace: true }); // Internal React route with replace option
    }
}

export default function CheckUserStatus({ user }: Props) {
    const flags = useFeatureFlags();
    const navigate = useNavigate();
    appLogger.setFeatureFlags({ flags: flags });

    const { data: representative, error } = useRepresentative(user.consumerResourceId);

    useEffect(() => {
        if (representative) {
            if (!representative.isActive) {
                redirectUser(user.consumerResourceId, '/usernotactive', 'Inactive Url', navigate);
            } else {
                const activeDealerRelationships = representative.relationships.filter(
                    dealerRelationship =>
                        dealerRelationship.isActive && dealerRelationship.isActiveBuyer
                );

                if (activeDealerRelationships.length === 0) {
                    redirectUser(
                        user.consumerResourceId,
                        `${process.env.AUCTIONS_WCS_URL}/Register/IncompleteRegistration`,
                        'Incomplete Registration',
                        navigate
                    );
                }
            }
        } else if (error) {
            const errorMessage = (error as Error).message;
            // If error.message is like '[...] {statusCode} [...]' when statusCode = 404.
            if (errorMessage.includes(' 404 ')) {
                redirectUser(
                    user.consumerResourceId,
                    `${process.env.AUCTIONS_WCS_URL}/Register/IncompleteRegistration`,
                    'Incomplete Registration',
                    navigate
                );
            }
        }
    }, [representative, error, user.consumerResourceId, navigate]);

    if (window.location && window.location.hostname.indexOf('wwwuat') >= 0) {
        const hasAccess = flags['mfe-uat-access'] ?? true;
        if (!hasAccess) {
            redirectUser(
                user.consumerResourceId,
                `${process.env.AUCTIONS_HOMEPAGE_URL}${window.location.pathname}${window.location.search}${window.location.hash}`,
                'Production Url',
                navigate
            );
        }
    }

    return <></>;
}
